import styled from '@emotion/styled'
import { colors } from '../../../../../__styling/settings/colors'
import { zIndex } from '../../../../../__styling/settings/z-index'
import {
    breakpoint,
    breakpointMax,
    calcRem,
} from '../../../../../__styling/style-functions'
import {
    visuallyhidden,
    transition,
} from '../../../../../__styling/style-mixins'
import { WebNavLink } from '@news-mono/web-common'
import { WAFootyLivesHere } from './assets/WAFootyLivesHere'

const sportsBreachVariantGrey = '#D8D8D8'
const save46Icon = require('./assets/save46.png')

export const StyledBreachContainer = styled('div')(({ theme }) => ({
    padding: calcRem(theme.margins.md, 0, theme.margins.lg),
    textAlign: 'center',
    [breakpoint('sm')]: {
        padding: calcRem(0, theme.margins.md, theme.margins.lg),
    },
    [breakpointMax('xs')]: {
        borderTop: `solid 2px ${sportsBreachVariantGrey}`,
    },
}))

export const StyledHeading = styled('h2')(visuallyhidden)

export const StyledPremiumLabel = styled('div')(({ theme }) => ({
    lineHeight: 1.5,
    color: colors.black,
    margin: '0 auto',
    marginBottom: calcRem(theme.margins.lg),
    fontSize: calcRem(14),
    fontFamily: theme.fonts.sansSerif,
}))

export const ultimatePackClassName =
    'SportBreachNotification_Ultimate_Sports_Pack'
export const everydayDigitalClassName =
    'SportBreachNotification_Everyday_Digital_Pack'

export const StyledSubscriptionContainer = styled('div')(({ theme }) => ({
    [`.${ultimatePackClassName}`]: {
        position: 'relative',

        div: {
            position: 'relative',
            h3: {
                // Save 46% Icon
                '&::before': {
                    width: calcRem(73),
                    height: calcRem(80),
                    top: calcRem(58),
                    position: 'absolute',
                    content: "''",
                    backgroundImage: `url(${save46Icon})`,
                    backgroundSize: 'contain',

                    '@media (max-width: 399px)': {
                        width: calcRem(50),
                        height: calcRem(55),
                        left: '-2%',
                    },
                    '@media (min-width: 400px) and (max-width: 499px)': {
                        left: '-2%',
                    },
                    '@media (min-width: 500px) and (max-width: 619px)': {
                        left: '6%',
                    },
                    '@media (min-width: 620px) and (max-width: 768px)': {
                        left: '18%',
                    },
                    '@media (min-width: 768px) and (max-width: 800px)': {
                        left: '-10%',
                    },
                    '@media (min-width: 801px)': {
                        left: '-5%',
                    },
                },
            },
        },

        '&::after': {
            position: 'absolute',
            content: "''",
            width: calcRem(2),
            height: `calc(100% - ${theme.margins.lg}px - ${theme.margins.lg}px)`,
            marginTop: calcRem(theme.margins.lg),
            marginBottom: calcRem(theme.margins.lg),
            backgroundColor: sportsBreachVariantGrey,
            right: calcRem(-1),

            [breakpointMax('sm')]: { display: 'none' },
        },

        [breakpointMax('sm')]: {
            borderBottom: `solid 2px ${sportsBreachVariantGrey}`,
        },
    },

    [breakpoint('sm')]: {
        display: 'flex',
    },
}))

export const StyledSubscription = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '100%',
    padding: 0,

    [breakpoint('xs')]: {
        flexBasis: '50%',
        padding: calcRem(0, theme.margins.sm),
    },

    [breakpoint('sm')]: {
        marginBottom: 0,
    },
}))

export const StyledSubscriptionWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    width: '100%',
    padding: calcRem(theme.margins.md, theme.margins.md),
    backgroundColor: colors.white,
}))

export const StyledSubscriptionHeading = styled('h3')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: colors.black,
    fontSize: calcRem(26),
    fontFamily: theme.fonts.sansSerifCond,
    margin: 0,

    [breakpoint('md')]: {
        fontSize: calcRem(32),
    },
}))

export const StyledSubscriptionInfoContainer = styled('p')({ margin: 0 })

export const StyledSubscriptionInfo = styled('span')<{
    bold?: boolean
    strikethrough?: boolean
}>(({ bold, strikethrough, theme }) => ({
    fontFamily: theme.fonts.sansSerifCond,
    fontWeight: bold ? 700 : undefined,
    textDecoration: strikethrough ? 'line-through' : undefined,
    fontSize: calcRem(19),
}))

export const StyledPlusIcon = styled('img')({
    position: 'absolute',
    width: calcRem(54),
})

export const StyledDayRange = styled('div')({
    position: 'relative',
    img: {
        left: -11,
    },
    p: {
        marginLeft: calcRem(30),
    },
})

export const StyledParagraph = styled('p')<{
    bold?: boolean
    marginTop?: boolean
}>(({ bold, marginTop, theme }) => ({
    // IE11 specific to wrap text
    width: '100%',
    fontSize: calcRem(13),
    fontWeight: bold ? 700 : undefined,
    margin: 0,
    marginTop: marginTop ? theme.margins.md : undefined,
}))

export const StyledParagraphMobileOnly = styled(StyledParagraph)({
    [breakpoint('xs')]: { display: 'none' },
})

export const StyledParagraphAboveMobile = styled(StyledParagraph)({
    [breakpointMax('xs')]: { display: 'none' },
})

export const StyledSubscribeButton = styled('button')(({ theme }) => ({
    backgroundImage: `linear-gradient(180deg, #0A70F3 0%, ${colors.thewest.westblue} 66%)`,
    borderRadius: calcRem(2),
    fontFamily: theme.fonts.sansSerif,
    fontSize: calcRem(20),
    color: colors.white,
    textAlign: 'center',
    display: 'block',
    flexGrow: 0,
    border: 0,
    padding: calcRem(theme.margins.sm, theme.margins.md),
    marginTop: calcRem(theme.margins.sm),
    marginBottom: calcRem(theme.margins.md),
    fontWeight: 700,
    lineHeight: 1.5,
    textDecoration: 'none',
    textTransform: 'uppercase',
    cursor: 'pointer',

    '&:hover, &:focus': {
        backgroundImage: 'none',
        outline: 'none',
    },

    '&:hover': {
        backgroundColor: colors.thewest.greyGorilla,
    },

    '&:focus': {
        backgroundColor: colors.thewest.greyGorilla,
        textDecoration: 'underline',
    },
}))

export const StyledLoginContainer = styled('span')(({ theme }) => ({
    button: {
        fontSize: calcRem(14),
        fontWeight: 700,
        color: colors.thewest.westblue,
        textDecoration: 'underline',
        '&:hover, &:focus': {
            color: colors.thewest.greyGorilla,
        },
        fontFamily: theme.fonts.sansSerif,
    },
}))

export const StyledLogInButton = styled('button')({
    padding: 0,
    margin: 0,
    background: 'none',
    border: 'none',
    color: colors.white,
    cursor: 'pointer',
    fontWeight: 700,
    textDecoration: 'underline',
    ...transition({ properties: ['color'] }),

    '&:hover, &:focus': {
        color: colors.thewest.hollywoodCerise,
    },

    '&:active': {
        outline: 'none', // dont want it to appear like a button, it should match link
    },
})

export const StyledLogInLink = StyledLogInButton.withComponent(WebNavLink)

export const StyledBreachFooter = styled('footer')(({ theme }) => ({
    color: colors.black,
    textAlign: 'center',
    padding: calcRem(theme.margins.lg, 0),
    borderTop: `solid 2px ${colors.thewest.westblue}`,
    borderBottom: `solid 2px ${colors.thewest.westblue}`,
    fontFamily: theme.fonts.sansSerif,
}))

export const StyledWAFootyLogo = styled(WAFootyLivesHere)({
    maxHeight: calcRem(80),
})

export const StyledSubcriptionSubtitle = styled('p')(({ theme }) => ({
    position: 'relative',
    fontSize: calcRem(30),
    marginBottom: 0,
    '&::before': {
        content: "''",
        display: 'block',
        borderTop: `solid 2px ${colors.thewest.westblue}`,
        width: '100%',
        height: 1,
        position: 'absolute',
        top: '50%',
        zIndex: zIndex.thewest.breachSportVariant.subtitleContainer,

        [breakpointMax('xs')]: { display: 'none' },
    },

    [breakpointMax('xs')]: {
        marginTop: calcRem(theme.margins.md),
        fontSize: calcRem(22),
    },
}))

export const StyledSubcriptionText = styled('span')({
    background: colors.white,
    padding: calcRem(0, 20),
    position: 'relative',
    zIndex: zIndex.thewest.breachSportVariant.subtitleText,

    [breakpointMax('xs')]: { padding: 0 },
})

export const StyledBreachFooterText = styled('p')({
    fontSize: calcRem(16),
    marginTop: 0,
    maxWidth: calcRem(600),
    marginLeft: 'auto',
    marginRight: 'auto',
})

export const StyledOutlineAnchor = styled('a')({
    display: 'inline-block',
    color: colors.thewest.westblue,
    lineHeight: 1.5,
    textDecoration: 'underline',
    cursor: 'pointer',
    ...transition({ properties: ['background', 'color'] }),

    '&:hover': {
        color: colors.thewest.greyGorilla,
    },

    '&:focus': {
        color: colors.thewest.greyGorilla,
    },
})

export const StyledContactDetails = styled('p')(({ theme }) => ({
    display: 'inline-block',
    margin: 0,
    fontFamily: theme.fonts.sansSerif,

    a: {
        textDecoration: 'underline',
        color: colors.thewest.westblue,
        ...transition({ properties: ['color'] }),
    },

    'a:hover, a:focus': {
        color: colors.thewest.greyGorilla,
        textDecoration: 'underline',
    },
}))
